import { ChangeEvent, useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { useAppDispatch } from '@/core/store/store';
import { Input } from '@/core/components/Input';
import { Icon } from '@/core/components/Icons';
import { applyAllFilters } from '@/core/store/reducers/filtersSlice';
import { FocusItem } from '@/core/components/FocusItem/FocusItem';
import { ReportReadSwitchStatus } from '@/core/interfaces/common';

import { getSelectedFocusSelector, selectFocus } from '@/features/Focus/store';
import { getUserSelector } from '@/features/Auth/store';
import { addMulticolumn } from '@/features/Reports/store';

import { isBrowser } from '@/utils/helpers';

import { FocusesContainer } from './FocusesList.styles';
import { FocusesListProps } from './FocusesList.types';

const getStorageKey = (userId: number | string, storageKey: string) =>
  `focus_order_${userId}_${storageKey}`;

export const FocusesList = ({ focuses, onCloseAllModals, storageKey }: FocusesListProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [orderedIds, setOrderedIds] = useState<Array<number>>([]);
  const dispatch = useAppDispatch();
  const selectedFocus = useSelector(getSelectedFocusSelector);
  const user = useSelector(getUserSelector);
  const { pathname } = useRouter();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    if (!isBrowser || !user?.id || !storageKey) {
      setOrderedIds([]);

      return;
    }

    try {
      const raw = localStorage.getItem(getStorageKey(user.id, storageKey));

      if (!raw) {
        setOrderedIds([]);

        return;
      }

      const parsed: unknown = JSON.parse(raw);

      if (Array.isArray(parsed) && parsed.every(id => Number.isFinite(id))) {
        setOrderedIds(parsed as Array<number>);
      } else {
        setOrderedIds([]);
      }
    } catch {
      setOrderedIds([]);
    }
  }, [user?.id, storageKey]);

  const displayFocuses = useMemo(() => {
    const known = new Map(focuses.map(focus => [focus.id, focus]));
    const headSet = new Set(orderedIds);
    const head = orderedIds.filter(id => known.has(id)).map(id => known.get(id)!);
    const tail = focuses.filter(focus => !headSet.has(focus.id));

    return [...head, ...tail];
  }, [focuses, orderedIds]);

  const visibleFocuses = useMemo(() => {
    if (!searchValue) {
      return displayFocuses;
    }

    const query = searchValue.toLowerCase();

    return displayFocuses.filter(focus => focus.name.toLowerCase().includes(query));
  }, [displayFocuses, searchValue]);

  const onSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleSelectFocus = useCallback(
    (id: number) => {
      onCloseAllModals?.();

      const newSelectedFocus = focuses.find(focus => focus.id === id);

      if (pathname === '/multilist') {
        if (!newSelectedFocus) {
          return;
        }

        dispatch(
          addMulticolumn({
            id,
            name: newSelectedFocus.name,
            horizonReports: [],
            lookoutReports: [],
            filters: newSelectedFocus.filters,
            readSwitchStatus: ReportReadSwitchStatus.UNREAD,
          })
        );

        return;
      }

      if (newSelectedFocus?.id === selectedFocus?.id) {
        return;
      }

      dispatch(selectFocus(id));

      if (newSelectedFocus?.filters) {
        dispatch(applyAllFilters(newSelectedFocus.filters));
      } else {
        dispatch(applyAllFilters({}));
      }
    },
    [dispatch, focuses, onCloseAllModals, pathname, selectedFocus?.id]
  );

  // Build a stable per-id click handler map so memoized FocusItem children
  // don't re-render on every drag-over frame due to a fresh arrow prop.
  const selectHandlers = useMemo(() => {
    const map = new Map<number, () => void>();

    visibleFocuses.forEach(focus => {
      map.set(focus.id, () => handleSelectFocus(focus.id));
    });

    return map;
  }, [visibleFocuses, handleSelectFocus]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const currentIds = displayFocuses.map(focus => focus.id);
      const oldIndex = currentIds.indexOf(Number(active.id));
      const newIndex = currentIds.indexOf(Number(over.id));

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const next = arrayMove(currentIds, oldIndex, newIndex);

      setOrderedIds(next);

      if (isBrowser && user?.id && storageKey) {
        localStorage.setItem(getStorageKey(user.id, storageKey), JSON.stringify(next));
      }
    },
    [displayFocuses, user?.id, storageKey]
  );

  const isDndEnabled = !searchValue && !!storageKey;

  return (
    <FocusesContainer>
      <Input
        placeholder="Search focuses"
        value={searchValue}
        onChange={onSearch}
        prefixIcon={<Icon name="Search" />}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visibleFocuses.map(focus => String(focus.id))}
          strategy={verticalListSortingStrategy}
        >
          {visibleFocuses.map(focus => (
            <FocusItem
              id={focus.id}
              isSelected={selectedFocus?.id === focus.id}
              name={focus.name}
              key={focus.id}
              isDraggable={isDndEnabled}
              onClick={selectHandlers.get(focus.id)!}
            />
          ))}
        </SortableContext>
      </DndContext>
    </FocusesContainer>
  );
};
